import {
  CheckInSlipSummary,
  printCheckInSlip,
  renderCheckInSlipHTML,
  type CheckInSlipData,
} from "@ths/shared/printing";
import { useCallback, useEffect, useRef, useState } from "react";
import type { FC } from "react";

import { trackBasecampEvent } from "../analytics";

interface KioskCheckInSlipScreenProps {
  data: CheckInSlipData;
  countdownSeconds: number;
  onComplete: () => void;
}

type PrintState = "printing" | "ok" | "unavailable" | "error";

const KioskCheckInSlipScreen: FC<KioskCheckInSlipScreenProps> = ({
  data,
  countdownSeconds,
  onComplete,
}) => {
  const [remaining, setRemaining] = useState(countdownSeconds);
  const [printState, setPrintState] = useState<PrintState>("printing");
  const printedRef = useRef(false);
  const completedRef = useRef(false);

  const runPrint = useCallback(async () => {
    setPrintState("printing");
    try {
      const result = await printCheckInSlip(renderCheckInSlipHTML(data));
      if (result.status === "ok") {
        setPrintState("ok");
        trackBasecampEvent("basecamp_kiosk_print_completed", {
          flow: "checkin",
          outcome: "success",
          printStatus: "ok",
          surface: "kiosk",
        });
      } else if (result.status === "unavailable") {
        setPrintState("unavailable");
        trackBasecampEvent("basecamp_kiosk_print_failed", {
          errorCategory: "print_failed",
          flow: "checkin",
          outcome: "failure",
          printStatus: "unavailable",
          surface: "kiosk",
        });
      } else {
        setPrintState("error");
        trackBasecampEvent("basecamp_kiosk_print_failed", {
          errorCategory: "print_failed",
          flow: "checkin",
          outcome: "failure",
          printStatus: "error",
          surface: "kiosk",
        });
      }
    } catch {
      setPrintState("error");
      trackBasecampEvent("basecamp_kiosk_print_failed", {
        errorCategory: "print_failed",
        flow: "checkin",
        outcome: "failure",
        printStatus: "error",
        surface: "kiosk",
      });
    }
  }, [data]);

  // Print once on mount.
  useEffect(() => {
    if (printedRef.current) return;
    printedRef.current = true;
    void runPrint();
  }, [runPrint]);

  // Countdown.
  useEffect(() => {
    if (remaining <= 0) {
      if (!completedRef.current) {
        completedRef.current = true;
        onComplete();
      }
      return;
    }
    const id = setTimeout(() => setRemaining((r) => r - 1), 1000);
    return () => clearTimeout(id);
  }, [remaining, onComplete]);

  const handleDone = () => {
    if (completedRef.current) return;
    completedRef.current = true;
    onComplete();
  };

  return (
    <div className="relative z-10 flex flex-1 flex-col items-center justify-center px-6 py-12">
      <CheckInSlipSummary data={data} />

      {printState === "printing" && (
        <div
          role="status"
          aria-live="polite"
          className="bc-fade-up mt-8 flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-white/40"
        >
          <span className="size-1.5 animate-pulse rounded-full bg-white/50" />
          Printing your slip…
        </div>
      )}

      {printState === "ok" && (
        <div
          role="status"
          aria-live="polite"
          className="bc-fade-up mt-8 flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-emerald-300/70"
        >
          <span className="size-1.5 rounded-full bg-emerald-300/80" />
          Slip printed
        </div>
      )}

      {(printState === "unavailable" || printState === "error") && (
        <div
          role="alert"
          className="bc-fade-up mt-8 flex max-w-md flex-col items-center gap-3 rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-center text-sm text-amber-200"
        >
          <p>
            {printState === "unavailable"
              ? "Printer unavailable — please see staff for a copy of your codes."
              : "Couldn't print slip — please see staff for a copy of your codes."}
          </p>
          <button
            type="button"
            onClick={() => {
              void runPrint();
            }}
            className="rounded-lg border border-amber-300/40 px-4 py-1.5 text-xs font-medium uppercase tracking-wider text-amber-100 transition-all hover:border-amber-300/60 hover:text-amber-50 active:scale-[0.98]"
          >
            Try again
          </button>
        </div>
      )}

      <div className="mt-12 flex flex-col items-center gap-3">
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-white/30">
          Returning to start in {remaining}s
        </p>
        <button
          type="button"
          onClick={handleDone}
          className="rounded-xl border border-white/[0.15] px-12 py-4 font-oswald text-lg font-medium uppercase tracking-wider text-white/80 transition-all hover:border-white/30 hover:text-white active:scale-[0.98]"
        >
          Done
        </button>
      </div>
    </div>
  );
};

export default KioskCheckInSlipScreen;
